import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../environments/environment";

import { UserPermission } from "../../models/userPermission.model";
import { BrandDefinition } from "../../models/brand-definition";
import { Observable } from "rxjs";
import { SpecialProgramUpdate } from "../../models/specialProgram-update";
import { Permission } from "../../models/permission.model";
import { Customer } from "../../models/customer.model";

@Injectable({
  providedIn: "root",
})
export class PortalAdminService {
  constructor(private authHttp: HttpClient) {}

  getCustomers(customer: string): Observable<Customer[]> {
    const params = new HttpParams().set('name', encodeURIComponent(customer));
    return this.authHttp.get<Customer[]>(
      environment.portalAdminConfig.baseApiUrl + "Customers/holding",
      { params: params }  
    );
  }

  getBranches(holdingAccountId: string): Observable<Customer[]> {
    return this.authHttp.get<Customer[]>(
      environment.portalAdminConfig.baseApiUrl +
        "Customers/holding/" +
        holdingAccountId +
        "/branches"
    );
  }

  getCompanyUsers(holdingAccountId: string) {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl + "users/" + holdingAccountId
    );
  }

  //Special Programs

  getPermissions(accountId: string): Observable<Permission[]> {
    return this.authHttp.get<Permission[]>(
      environment.portalAdminConfig.baseApiUrl + "SpecialPrograms/" + accountId
    );
  }

  getProgramMarkdown(
    programCode: string,
    year: number,
    program: string,
    country: string
  ) {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl + 
        `SpecialPrograms/${programCode}/year/${year}?program=${encodeURIComponent(program)}&country=${country || ""}`
    );
  }

  updateProgramMarkdown(specialProgram: SpecialProgramUpdate) {
    return this.authHttp.put(
      environment.portalAdminConfig.baseApiUrl + "SpecialPrograms/",
      specialProgram
    );
  }

  //Program Access Endpoints

  getUserPermissions(holdingAccountId: string) {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/" +
        holdingAccountId
    );
  }

  getBranchUserPermissions(accountId: string): Observable<UserPermission[]> {
    return this.authHttp.get<UserPermission[]>(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/Accounts/" +
        accountId
    );
  }

  getUserProgramAccess(holdingAccountId: string, userId: number) {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/" +
        holdingAccountId +
        "/user/" +
        userId
    );
  }

  addUserPermissions(holdingAccountId: string, body: UserPermission) {
    const userProgramAccess = body;
    return this.authHttp.post(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/" +
        holdingAccountId,
      userProgramAccess
    );
  }

  addBranchUserPermissions(accountId: string, body: UserPermission) {
    const userProgramAccess = body;
    return this.authHttp.post(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/Account/" +
        accountId,
      userProgramAccess
    );
  }

  updateUserPermissions(holdingAccountId: string, body: UserPermission) {
    const userProgramAccess = body;
    return this.authHttp.put(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/" +
        holdingAccountId,
      userProgramAccess
    );
  }

  updateBranchUserPermissions(accountId: string, body: UserPermission) {
    const userProgramAccess = body;
    return this.authHttp.put(
      environment.portalAdminConfig.baseApiUrl +
        "ProgramAccess/Account/" +
        accountId,
      userProgramAccess
    );
  }

  //Trade Agreement Endpoints

  getBrandDefinitions() {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl +
        "TradeAgreement/brandDefinitions"
    );
  }

  updateBrandDefinitions(body: BrandDefinition) {
    return this.authHttp.put(
      environment.portalAdminConfig.baseApiUrl +
        "TradeAgreement/brandDefinition",
      body
    );
  }

  getSourceSystems(){
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl +
      "TradeAgreement/SourceSystems"
    );
  }

  //MRC Endpoints

  getUsers() {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl + "Mrc/users"
    );
  }

  getGroups() {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl + "Mrc/groups"
    );
  }

  updateUser(userId: string, mrcdata) {
    return this.authHttp.put(
      environment.portalAdminConfig.baseApiUrl + "Mrc/" + userId + "/mrcdata",
      mrcdata
    );
  }

  createUser(userId: string, mrcdata) {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl + "Mrc/" + userId + "/mrcdata",
      mrcdata
    );
  }

  checkEmail(email: string) {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl + "Mrc/search/" + email
    );
  }

  addUser(userId: string, mrcdata) {
    return this.authHttp.post(
      environment.portalAdminConfig.baseApiUrl + "Mrc/" + userId + "/mrcdata",
      mrcdata
    );
  }

  parseRecommitOrders(payload) {
    return this.authHttp.post(
      environment.portalAdminConfig.baseApiUrl +
        "RecommitOrders/ParseRecommitOrders",
      payload
    );
  }

  submitRecommitOrders(payload) {
    return this.authHttp.post(
      environment.portalAdminConfig.baseApiUrl +
        "RecommitOrders/SubmitRecommitOrders",
      payload
    );
  }

  hasFailedRecommitOrders() {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl +
        "RecommitOrders/HasFailedRecommitOrders"
    );
  }

  getFailedRecommitOrders() {
    return this.authHttp.get(
      environment.portalAdminConfig.baseApiUrl +
        "RecommitOrders/GetFailedRecommitOrders"
    );
  }

  processFailedRecommitOrders(payload) {
    return this.authHttp.post(
      environment.portalAdminConfig.baseApiUrl +
        "RecommitOrders/ProcessFailedRecommitOrders",
      payload
    );
  }
}
